// Registry of the types and full validation of a creation: schema, then type-specific checks.
import { CreationInput, type CreationKind, type CreationOf, type Fact, type Issue } from '@gruvetto/contracts';
import type { KindContext, KindDef } from './kind.js';
import { workoutKind } from './kinds/workout.js';
import { planKind } from './kinds/plan.js';
import { trackKind } from './kinds/track.js';
import { modeKind } from './kinds/mode.js';
import { cosmeticKind } from './kinds/cosmetic.js';
import { gameKind } from './kinds/game.js';
import { itemKind } from './kinds/item.js';
import { themeKind } from './kinds/theme.js';

export const KINDS: { [K in CreationKind]: KindDef<K> } = { workout: workoutKind, plan: planKind, track: trackKind, mode: modeKind, item: itemKind, theme: themeKind, cosmetic: cosmeticKind, game: gameKind };
export const kindOf = <K extends CreationKind>(k: K): KindDef<K> => KINDS[k];

export interface Validation { ok: boolean; creation: CreationInput | null; issues: Issue[]; facts: Fact[] }

/** Validates a creation coming from outside (AI, network, draft): never an exception, always a readable list. */
export function validateCreation(raw: unknown, ctx: KindContext): Validation {
  const parsed = CreationInput.safeParse(raw);
  if (!parsed.success) return { ok: false, creation: null, facts: [], issues: parsed.error.issues.slice(0, 12).map(i => ({ level: 'error', message: i.message, path: i.path.join('.') })) };
  const c = parsed.data, k = KINDS[c.kind] as KindDef;
  let issues: Issue[], facts: Fact[] = [];
  try { issues = k.check(c as CreationOf<CreationKind>, ctx); facts = issues.some(i => i.level === 'error') ? [] : k.facts(c as CreationOf<CreationKind>, ctx); }
  catch (e) { issues = [{ level: 'error', message: `Inconsistent creation: ${(e as Error).message}` }]; }
  return { ok: !issues.some(i => i.level === 'error'), creation: c, issues, facts };
}
